'use client'

import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { forwardRef, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { otherApi } from '@/api'

type Props = {
  value?: string | undefined
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  optional?: boolean
  onChange: (val: string) => void
} & React.ComponentPropsWithoutRef<'input'>

export const LocationAutoComplete = forwardRef<HTMLInputElement, Props>((props: Props, ref: React.Ref<HTMLInputElement>) => {
  const { value, onChange } = props

  // query
  const [query, setQuery] = useState('')
  const { data } = useQuery({
    queryKey: [query],
    queryFn: () => otherApi.location(query),
    refetchOnWindowFocus: false,
  })

  // set result
  const [option, setOption] = useState<string[]>([])
  useEffect(() => {
    setOption(data?.data || [])
  }, [data])

  const errMsg = _.get(props, 'data-error')
  const inputId = props.id || 'locationAutoComplete'
  const errorId = `${inputId}-error`

  return (
    <Combobox as='div' value={value} onChange={(val) => onChange(val || '')}>
      <label htmlFor={inputId} className='form-label'>
        Location <span className='ml-1.5 font-light text-zinc-600 dark:text-zinc-400'>(City, State)</span>
      </label>
      <div className={twMerge('relative mt-1 text-sm')}>
        <ComboboxInput
          id={inputId}
          as='input'
          className='form-listbox'
          onChange={(e) => setQuery(e.target.value)}
          ref={ref}
          aria-invalid={!!errMsg}
          aria-describedby={errMsg ? errorId : undefined}
          autoComplete='off'
          {..._.omit(props, ['value', 'onChange', 'data-error'])}
        />
        <ComboboxOptions transition className='form-options'>
          {_.size(query) > 0 && (
            <ComboboxOption key={-1} value={query} className='form-option'>
              {query}
            </ComboboxOption>
          )}
          {_.map(option, (d, idx) => {
            return (
              <ComboboxOption key={idx} value={d} className='form-option'>
                <span className='block truncate group-data-selected:font-semibold'>{d}</span>
              </ComboboxOption>
            )
          })}
        </ComboboxOptions>
      </div>
      {errMsg && (
        <div id={errorId} className='form-error-message' aria-live='polite'>
          {errMsg}
        </div>
      )}
    </Combobox>
  )
})
